import type { CSSProperties } from 'react';
import { createContext, useCallback, useSyncExternalStore } from 'react';

import { getBackgroundColor } from '@/utils/background';

import { NAVBAR_MENU_DEFAULTS_V2 } from './navbarMenuElement/NavbarMenu.constants';

export type NavbarScrollDirection = 'up' | 'down' | 'none';

export type NavbarScrollSnapshot = {
  scrollY: number;
  direction: NavbarScrollDirection;
};

export type NavbarScrollStyle = 'none' | 'solid' | 'glass';

export type NavbarScrollContextValue = {
  isScrolledPastOffset: boolean;
  scrollStyle: NavbarScrollStyle;
};

export const GLASS_NAVBAR_BLUR = '12px';
export const HIDE_ON_SCROLL_THRESHOLD = 60;

const STATIC_SNAPSHOT: NavbarScrollSnapshot = { scrollY: 0, direction: 'none' };

export const getWindowScrollY = (): number => {
  if (typeof window === 'undefined') return 0;
  return Math.max(window.scrollY, document.documentElement.scrollTop);
};

const DEFAULT_SCROLL_CONTEXT: NavbarScrollContextValue = {
  isScrolledPastOffset: false,
  scrollStyle: 'none',
};

let snapshot: NavbarScrollSnapshot = STATIC_SNAPSHOT;
let rafId: number | null = null;
let lastScrollY = 0;
const listeners = new Set<() => void>();

const getScrollSnapshot = (): NavbarScrollSnapshot => snapshot;

const notifyListeners = () => {
  for (const listener of listeners) {
    listener();
  }
};

const handleScroll = () => {
  if (rafId !== null) return;

  rafId = requestAnimationFrame(() => {
    rafId = null;
    const scrollY = getWindowScrollY();
    const direction: NavbarScrollDirection = scrollY > lastScrollY ? 'down' : scrollY < lastScrollY ? 'up' : 'none';
    lastScrollY = scrollY;

    if (snapshot.scrollY === scrollY && snapshot.direction === direction) return;

    snapshot = { scrollY, direction };
    notifyListeners();
  });
};

const attachScrollListener = () => {
  if (typeof window === 'undefined') return;

  lastScrollY = getWindowScrollY();
  snapshot = { scrollY: lastScrollY, direction: 'none' };
  window.addEventListener('scroll', handleScroll, { passive: true });
  notifyListeners();
};

const detachScrollListener = () => {
  if (typeof window === 'undefined') return;

  window.removeEventListener('scroll', handleScroll);
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  snapshot = STATIC_SNAPSHOT;
  lastScrollY = 0;
};

const subscribe = (listener: () => void) => {
  const hadListeners = listeners.size > 0;
  listeners.add(listener);

  if (!hadListeners) {
    attachScrollListener();
  }

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0) {
      detachScrollListener();
    }
  };
};

/** ponytail: test-only hook to assert listener attach/detach without mounting React */
export const __navbarScrollStoreTestUtils = {
  getSubscriberCount: () => listeners.size,
  reset: () => {
    detachScrollListener();
    listeners.clear();
  },
};

export const useNavbarScrollStore = (enabled: boolean): NavbarScrollSnapshot => {
  const subscribeWhenEnabled = useCallback(
    (listener: () => void) => (enabled ? subscribe(listener) : () => {}),
    [enabled],
  );

  const getSnapshotWhenEnabled = useCallback(() => (enabled ? getScrollSnapshot() : STATIC_SNAPSHOT), [enabled]);

  return useSyncExternalStore(subscribeWhenEnabled, getSnapshotWhenEnabled, () => STATIC_SNAPSHOT);
};

export const NavbarScrollContext = createContext<NavbarScrollContextValue>(DEFAULT_SCROLL_CONTEXT);

type NavbarScrollVariantBaseAttrs = {
  background?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderColor?: string;
  style?: string;
};

const forceOpaqueBackground = (color?: string): string => {
  const resolved = getBackgroundColor(color ?? NAVBAR_MENU_DEFAULTS_V2.background);
  if (!resolved || resolved === 'transparent') {
    return NAVBAR_MENU_DEFAULTS_V2.background;
  }

  const hex8Match = /^#([0-9A-Fa-f]{6})[0-9A-Fa-f]{2}$/.exec(resolved);
  if (hex8Match) {
    return `#${hex8Match[1]}FF`;
  }

  return resolved;
};

export const computeHiddenOnScroll = (
  scroll: NavbarScrollSnapshot,
  enabled: boolean,
  stateRef: { current: boolean },
): boolean => {
  if (!enabled) {
    stateRef.current = false;
    return false;
  }

  if (scroll.scrollY < HIDE_ON_SCROLL_THRESHOLD) {
    stateRef.current = false;
    return false;
  }

  if (scroll.direction === 'down') {
    stateRef.current = true;
  } else if (scroll.direction === 'up') {
    stateRef.current = false;
  }

  return stateRef.current;
};

export const getNavbarScrollVariantStyles = (
  scrollStyle: NavbarScrollStyle,
  baseAttrs: NavbarScrollVariantBaseAttrs,
): CSSProperties & Record<string, string | number | undefined> => {
  if (scrollStyle === 'solid') {
    const opaqueBackground = forceOpaqueBackground(baseAttrs.background ?? NAVBAR_MENU_DEFAULTS_V2.background);

    return {
      background: opaqueBackground,
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      '--background': opaqueBackground,
      '--box-shadow': 'none',
      ...(baseAttrs.style === 'border'
        ? {
            borderColor: baseAttrs.borderColor ?? NAVBAR_MENU_DEFAULTS_V2.borderBorderColor,
            borderWidth: baseAttrs.borderWidth ?? NAVBAR_MENU_DEFAULTS_V2.borderBorderWidth,
            borderStyle: baseAttrs.borderStyle ?? NAVBAR_MENU_DEFAULTS_V2.borderBorderStyle,
          }
        : {}),
      '--blur-background': '0px',
      '--blur-opacity': '0',
    };
  }

  if (scrollStyle === 'glass') {
    return {
      background: 'rgba(255,255,255,0.65)',
      backdropFilter: `blur(${GLASS_NAVBAR_BLUR})`,
      WebkitBackdropFilter: `blur(${GLASS_NAVBAR_BLUR})`,
      '--background': '#FFFFFF00',
      '--blur-background': GLASS_NAVBAR_BLUR,
      '--blur-opacity': '0.65',
      '--box-shadow': 'none',
    };
  }

  return {};
};
